<!-- Sidebar at background level -->
<div class="sidebar-shell fixed left-0 top-0 h-screen z-10">
  <!-- Sidebar Container -->
  <div
    [style.width.px]="sidebarService.width()"
    [style.transition]="sidebarService.isResizing() ? 'none' : 'width 300ms ease-in-out'"
    class="h-full flex flex-col bg-sidebar"
  >
    <!-- Header -->
    <div class="p-4 border-b border-sidebar-border">
      @if (!isCollapsed()) {
        <div class="flex items-center gap-2">
          <!-- Organization Selector or back link -->
          @if (isOnUserPage()) {
            <a hlmBtn variant="ghost" routerLink="/organizations"
              class="min-w-0 flex-1 justify-start gap-2 px-2 text-sm text-foreground"
              aria-label="Zur Organisationsauswahl">
              <ng-icon name="lucideArrowLeft" class="size-4" />
              <span class="truncate">Organisationen</span>
            </a>
          } @else if (isOrganizationLoading()) {
            <!-- Loading State -->
            <div
              class="flex-1 min-w-0 h-10 bg-muted animate-pulse rounded-md flex items-center justify-center"
            >
              <span class="text-xs text-muted-foreground">Lädt...</span>
            </div>
          } @else {
            <app-organization-selector class="flex-1 min-w-0"></app-organization-selector>
          }

          <button
            hlmBtn
            variant="ghost"
            size="icon"
            class="!size-8 shrink-0"
            aria-label="Sidebar einklappen"
            (click)="toggleSidebar()"
          >
            <ng-icon name="lucidePanelLeft" class="w-[18px] h-[18px] text-muted-foreground"></ng-icon>
          </button>
        </div>
      } @else {
        <!-- Collapsed organization or user-page back link -->
        <div class="flex flex-col items-center gap-2.5">
          @if (isOnUserPage()) {
            <a hlmBtn variant="ghost" size="icon" routerLink="/organizations"
              class="size-8" aria-label="Zur Organisationsauswahl" title="Zur Organisationsauswahl">
              <ng-icon name="lucideArrowLeft" class="size-4" />
            </a>
          } @else {
            <div
              class="min-w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
              [style.background-color]="organizationColor()"
            >
              {{ organizationInitials }}
            </div>
          }

          <button
            hlmBtn
            variant="ghost"
            size="icon"
            class="!size-8"
            aria-label="Sidebar einblenden"
            (click)="toggleSidebar()"
          >
            <ng-icon name="lucidePanelLeft" class="w-[18px] h-[18px] text-muted-foreground"></ng-icon>
          </button>
        </div>
      }
    </div>

    <!-- Navigation -->
    <div class="flex-1 overflow-y-auto py-4">
      @if (isOrganizationLoading()) {
        <!-- Loading skeleton for navigation -->
        <div class="px-4 space-y-4">
          <div class="h-8 bg-muted animate-pulse rounded"></div>
          <div class="h-8 bg-muted animate-pulse rounded"></div>
          <div class="h-8 bg-muted animate-pulse rounded"></div>
          <div class="h-8 bg-muted animate-pulse rounded"></div>
        </div>
      } @else {
        <nav class="px-2 space-y-6">
          <!-- Platform Section -->
          @for (section of sections(); track section) {
            @if (!isCollapsed()) {
              <div class="px-3 mb-2">
                <h3 class="ks-eyebrow">
                  {{ section.title }}
                </h3>
              </div>
            }

            <ul class="space-y-1">
              @for (item of section.items; track item) {
                <li>
                  <!-- Main Item -->
                  <div class="relative">
                    <button
                      [routerLink]="item.route || null"
                      routerLinkActive
                      #rla="routerLinkActive"
                      (click)="handleItemClick(item)"
                      hlmBtn
                      variant="ghost"
                      class="w-full text-sm text-muted-foreground"
                      [class.justify-center]="isCollapsed()"
                      [class.justify-start]="!isCollapsed()"
                      [class.!font-bold]="rla.isActive && item.route"
                      [class.text-foreground]="rla.isActive && item.route"
                      [class.bg-muted]="rla.isActive && item.route"
                      [attr.aria-label]="item.label"
                      [attr.aria-expanded]="item.children ? item.expanded : null"
                      [attr.title]="item.label"
                    >
                      <ng-icon
                        [name]="item.icon"
                        class="w-4 h-4"
                        [class.mr-3]="!isCollapsed()"
                      ></ng-icon>
                      @if (!isCollapsed()) {
                        <span class="min-w-0 flex-1 truncate text-left">{{ item.label }}</span>
                      }
                      @if (!isCollapsed() && item.children) {
                        <ng-icon
                          [name]="
                            item.expanded
                              ? 'lucideChevronDown'
                              : 'lucideChevronRight'
                          "
                          class="w-4 h-4 ml-auto transition-transform"
                        >
                        </ng-icon>
                      }
                    </button>
                  </div>

                  <!-- Children -->
                  @if (item.children && item.expanded && !isCollapsed()) {
                    <div class="ml-6 mt-1 space-y-1">
                      @for (child of item.children; track child) {
                        <button
                          [routerLink]="child.route"
                          routerLinkActive
                          #rla="routerLinkActive"
                          hlmBtn
                          variant="ghost"
                          class="w-full justify-start h-8 px-3 text-sm text-muted-foreground"
                          [class.!font-bold]="rla.isActive"
                          [class.text-foreground]="rla.isActive"
                          [class.bg-muted]="rla.isActive"
                        >
                          {{ child.label }}
                        </button>
                      }
                    </div>
                  }
                </li>
              }
            </ul>
          }
        </nav>
      }
    </div>

    <!-- Footer -->
    <div class="p-4 border-t border-sidebar-border">
      <!-- User Profile -->
      <div
        class="flex items-center gap-3"
        [class.justify-center]="isCollapsed()"
      >
        <app-avatar />
        @if (!isCollapsed()) {
          <div class="flex-1 min-w-0">
            <p class="text-sm font-normal text-foreground truncate">
              {{ userName }}
            </p>
            <p class="text-xs text-muted-foreground truncate">
              {{ userEmail }}
            </p>
          </div>
          <!-- Dropdown Container with relative positioning -->
          <div class="relative" [attr.data-dropdown-container]="'user-menu'">
            <button
              hlmBtn
              variant="ghost"
              size="sm"
              class="h-8 w-8 p-0 flex items-center justify-center"
              aria-label="Benutzermenü öffnen"
              (click)="toggleDropdown()"
            >
              <ng-icon
                name="lucideEllipsis"
                class="w-4 h-4 text-muted-foreground"
              ></ng-icon>
            </button>

            <!-- User Menu Dropdown -->
            @if (isOpen()) {
              <div class="absolute bottom-[calc(100%+0.5rem)] right-0 z-50">
                <app-user-account-menu (selected)="closeDropdown()" />
              </div>
            }
          </div>
        }
      </div>
    </div>
  </div>

  <div role="separator" tabindex="0" aria-orientation="vertical" aria-label="Sidebarbreite anpassen"
    [attr.aria-valuenow]="sidebarService.width()"
    [attr.aria-valuemin]="sidebarService.collapsedWidth"
    [attr.aria-valuemax]="sidebarService.maxWidth"
    [attr.aria-valuetext]="isCollapsed() ? 'Eingeklappt' : sidebarService.width() + ' Pixel'"
    class="group absolute -right-1 top-0 z-20 hidden h-full w-3 cursor-col-resize touch-none md:block"
    (pointerdown)="onResizeStart($event)"
    (pointermove)="onResizeMove($event)"
    (pointerup)="onResizeEnd($event)"
    (pointercancel)="onResizeEnd($event)"
    (lostpointercapture)="onResizeCancel()"
    (keydown)="onResizeKeydown($event)">
    <span aria-hidden="true" class="pointer-events-none absolute left-1/2 top-1/2 h-10 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-border opacity-0 transition-[opacity,background-color] group-hover:bg-primary/60 group-hover:opacity-100 group-focus:bg-primary group-focus:opacity-100 group-active:bg-primary group-active:opacity-100"></span>
  </div>
</div>
